/** @jsxImportSource preact */
import { useEffect, useState } from 'preact/hooks'
import { callApi } from '../api'

/**
 * Who can grant and revoke waivers, and change the switches on the All fraud checks tab.
 *
 * Two groups, shown separately because they behave differently. **Admins** come from deployment
 * config and cannot be edited here — that list is what stays readable when the database is not,
 * so somebody can always add the first agent and a failed migration cannot lock out the people
 * who would fix it. **Agents** are added here by an admin.
 */

interface Agent {
  email: string
  addedBy: string
  addedAt: string
}

interface Approved {
  admins: string[]
  agents: Agent[]
  you: string | null
  youAreAdmin: boolean
  youArePermitted: boolean
}

const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000))

export function FraudOperators() {
  const [data, setData] = useState<Approved | null>(null)
  const [failed, setFailed] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try {
      setData(await callApi<Approved>('/bookings/fraud/waiver-operators', {}, 'Could not load the approved agents'))
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const add = async () => {
    setBusy(true)
    setError('')
    try {
      await callApi(
        '/bookings/fraud/waiver-operators',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        },
        'Could not add that agent',
      )
      setEmail('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that agent')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (target: string) => {
    if (!confirm(`Remove ${target}? They will no longer be able to grant or revoke waivers.`)) return

    setBusy(true)
    setError('')
    try {
      await callApi(
        `/bookings/fraud/waiver-operators/${encodeURIComponent(target)}`,
        { method: 'DELETE' },
        'Could not remove that agent',
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that agent')
    } finally {
      setBusy(false)
    }
  }

  if (failed) {
    return (
      <p className="text-sm text-red-700">
        <strong>Could not load the approved agents.</strong> This is not the same as there being none.
      </p>
    )
  }

  if (!data) {
    return <p className="text-sm text-gray-500">Loading…</p>
  }

  const isYou = (address: string) => data.you?.toLowerCase() === address.toLowerCase()

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-800 mb-1">Approved agents</h2>
      <p className="mb-4 text-sm text-gray-600">
        These people can grant and revoke waivers, and change the switches on <strong>Eligibility rules</strong>.
        Everyone else can see those screens but not act on them.
      </p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}

      {!data.youArePermitted && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
          <strong>You are not approved.</strong> You can see everything here, but granting a waiver or changing a switch
          will be refused. Ask one of the admins below.
        </div>
      )}

      <h3 className="text-sm font-semibold text-gray-800 mb-2">Agents</h3>
      {data.agents.length === 0 ? (
        <p className="mb-3 text-sm text-gray-500">No agents have been added. Only the admins below can act.</p>
      ) : (
        <ul className="mb-3 border border-gray-200 rounded-md divide-y divide-gray-200">
          {data.agents.map(agent => (
            <li
              key={agent.email}
              className="px-4 py-2.5 text-sm flex flex-wrap items-center justify-between gap-2"
            >
              <span className="text-gray-800">
                {agent.email}
                {isYou(agent.email) && (
                  <span className="ml-2 text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                    You
                  </span>
                )}
                <span className="block text-xs text-gray-500">
                  Added by {agent.addedBy}, {daysSince(agent.addedAt)} days ago
                </span>
              </span>
              {data.youAreAdmin && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => remove(agent.email)}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {data.youAreAdmin ? (
        <div className="mb-8 flex flex-wrap gap-2 items-center">
          <input
            id="agent-email"
            type="email"
            value={email}
            placeholder="name@paylatertravel.com.au"
            onInput={e => setEmail((e.target as HTMLInputElement).value)}
            aria-label="Email address of the agent to approve"
            className="flex-1 min-w-56 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            type="button"
            disabled={busy || !email.trim()}
            onClick={add}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-300"
          >
            {busy ? 'Saving…' : 'Approve agent'}
          </button>
        </div>
      ) : (
        <p className="mb-8 text-sm text-gray-500">Only an admin can add or remove agents.</p>
      )}

      <h3 className="text-sm font-semibold text-gray-800 mb-2">Admins</h3>
      {data.admins.length === 0 ? (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
          <strong>No admins are configured.</strong> Nobody can grant a waiver or add an agent until that is fixed in
          the stack config.
        </div>
      ) : (
        <ul className="mb-4 border border-gray-200 rounded-md divide-y divide-gray-200">
          {data.admins.map(admin => (
            <li
              key={admin}
              className="px-4 py-2.5 text-sm text-gray-800 flex items-center justify-between gap-2"
            >
              <span>{admin}</span>
              {isYou(admin) && (
                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">You</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-sm text-gray-600">
        Admins are deployment configuration (<code className="text-xs">fraud_waiver.operators</code>), so changing them
        takes an engineer and a deploy. Keep it short &mdash; it exists so somebody can always act, including when the
        database cannot be read. Add the rest of the team as agents above.
      </p>
      <p className="mt-2 text-sm text-gray-600">
        Nothing prunes either list automatically. When somebody moves off this team their access stays until it is
        removed, so both are worth reading alongside the monthly waiver review.
      </p>
    </div>
  )
}
